import { FC, useEffect, useState } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { DelegatedKeyCard } from "@/components/DelegatedKeyCard";
import { delegatedKeyNeedsUser } from "@/hooks/useDelegatedKeyStatus";


/**
 * Raise the delegated-key prompt over the page instead of leaving it in the
 * balance panel.
 *
 * Inline, this card is the second item in a column that scrolls (`SwapPanel`
 * is tall, and the left column is `overflow-y-auto`), so on a normal viewport
 * it sits below the fold -- and it is the only control that restores the
 * account. A user in this state sees empty balances, a "Add funds to continue"
 * button, and no reason for either, which reads as the site being broken while
 * their deposit has in fact settled.
 *
 * Dismissable, because being unable to read balances should not stop anyone
 * looking at the market. Dismissal lasts until the situation resolves and
 * happens again; the inline card stays behind it as the permanent affordance.
 */
export const DelegatedKeyModal: FC = () => {
  const {
    delegatedKey,
    delegatedKeyCanSign,
    signDelegatedKey,
    dismissDelegatedKeySigning,
  } = useGummiwormContext();

  const [dismissed, setDismissed] = useState(false);
  const needsUser = delegatedKeyNeedsUser(delegatedKey.status);

  // Re-arm once the account is readable again, so a key that stops working
  // later prompts again rather than staying silenced by a click from an
  // earlier session of the same page.
  useEffect(() => {
    if (!needsUser) setDismissed(false);
  }, [needsUser]);

  const open = needsUser && !dismissed;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDismissed(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Connect your account"
      onClick={() => setDismissed(true)}
    >
      <div
        className="w-full max-w-md"
        onClick={(event) => event.stopPropagation()}
      >
        <DelegatedKeyCard
          state={delegatedKey}
          canSign={delegatedKeyCanSign}
          onSign={() => void signDelegatedKey()}
          onDismiss={dismissDelegatedKeySigning}
        />
        <button
          type="button"
          className="mt-3 w-full text-xs text-sr-neutral-400 underline underline-offset-2 hover:text-white"
          onClick={() => setDismissed(true)}
        >
          Not now — let me look around first
        </button>
      </div>
    </div>
  );
};
